import { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { usePendingImportHandoff } from '@/hooks/usePendingImportHandoff';
import { clearImportHandoff, importHandoffProjectPath } from '@/lib/importHandoff';
import { claimImportHandoff } from '@/hooks/useImportHandoffClaim';

// Lupina (`DashboardLayout`) je v vstopnem grafu (T2883/T2908), okno za nov
// projekt pa vleče obrazec in formate — naloži se šele ob kliku.
const NewProjectDialog = lazy(() => import('../projects/NewProjectDialog'));

/**
 * T3077 — pasica »Vaš popis s spletne strani čaka na uvoz«.
 *
 * Vidna v prijavljenem delu aplikacije (`DashboardLayout`), dokler čaka predaja
 * (`lib/importHandoff`). »Ustvari projekt in uvozi« odpre obstoječe okno za nov
 * projekt, ki po ustvarjanju pelje v popis s `?uvoz=predaja` — tam
 * `useImportHandoffClaim` datoteko prevzame in odpre čarovnik uvoza.
 * »Zavrzi« datoteko prevzame in odvrže (V10 varnostnega pregleda #505), da jo
 * strežnik izbriše takoj, ne šele po roku; lokalni vnos se pobriše v vsakem
 * primeru. Besedilo pove, od kod je datoteka (V8) — kdor povezave ni odprl sam,
 * jo prepozna kot tujo in zavrže.
 */
export default function ImportHandoffBanner() {
  const { t, i18n } = useTranslation();
  const pending = usePendingImportHandoff();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!pending) return null;

  const discard = (token: string) => {
    // Odgovor ni pomemben: tudi ob napaki strežnik datoteko izbriše po roku.
    claimImportHandoff(token).catch(() => undefined);
    clearImportHandoff();
  };

  const until = new Date(pending.expiresAt).toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        data-testid="import-handoff-banner"
        className="flex-shrink-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[var(--bp-border)] bg-[var(--bp-surface-2)] px-4 py-2 text-body text-[var(--bp-text-1)]"
      >
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 shrink-0 text-[var(--bp-accent-text)]" aria-hidden />
          <span>{t('import_v2.handoff.banner', { time: until })}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
            {t('import_v2.handoff.create_and_import')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => discard(pending.token)}>
            {t('import_v2.handoff.discard')}
          </Button>
        </div>
      </div>
      {dialogOpen && (
        <Suspense fallback={null}>
          <NewProjectDialog
            onClose={() => setDialogOpen(false)}
            createdPath={importHandoffProjectPath}
          />
        </Suspense>
      )}
    </>
  );
}
